import * as Color from "color"
import PptxGenJS, {
  Slide,
  ChartOptions,
  ChartBarDataset,
  ChartMultiTypeItemOptions,
  ChartMultiTypeItem,
  ChartMultiTypeOptions
} from "pptxgenjs"
import { createGlobalChartData, createInhabitantsChartData } from "./utils"
import { createAllocationsChartData } from "../tax-generator/utils"
import { ReportConfiguration } from "../../../configuration"
import * as Constants from "../../pptx-configuration/constants"
import { getChartBaseOptions, getChartColors } from "../../pptx-configuration/charts"
import { MasterSlide } from "../../pptx-configuration/master-slides"
import {
  getPlaceholderChartBodyOptions,
  Placeholder
} from "../../pptx-configuration/placeholders"
import { DocumentSection } from "../../types"
import { formatTitle, createDemographyData } from "../../utils"
import * as AllocationDomain from "../../../../../domain/sfp/allocation-domain"
import { LocalAuthority } from "../../../../../model"
import { ColorsHelper } from "../../../../../util/colors"

export function checkAllocationsSection(configuration: ReportConfiguration): boolean {
  const charts = configuration.observation.allocationsCharts
  return charts.global || !!(charts.allocations && charts.allocations.length)
}

export function createAllocationsSection(
  pptx: PptxGenJS,
  configuration: ReportConfiguration,
  numbering: number[],
  localAuthority: LocalAuthority,
  years: number[]
): DocumentSection {
  const title = formatTitle("Dotations", numbering)
  const slides: Slide[] = []
  const referenceYear = configuration.generalities.referenceYear
  const placeholderBodyOptions = getPlaceholderChartBodyOptions()
  const baseColor = new Color(configuration.table.header.backgroundColor)

  if (configuration.observation.allocationsCharts.global) {
    const globalSlide = pptx.addNewSlide(MasterSlide.Chart)

    globalSlide.addText(title, {
      placeholder: Placeholder.Title
    })

    const globalChartTitle = `Année ${referenceYear}`

    const globalChartData = createGlobalChartData(
      globalChartTitle,
      localAuthority.observations,
      referenceYear
    )

    const globalChartOptions: ChartOptions = {
      ...getChartBaseOptions(placeholderBodyOptions),
      showPercent:true,
      dataLabelFontBold:true,
      title: globalChartTitle,
	  chartColors: getChartColors(),
    }

    globalSlide.addChart(pptx.charts.PIE, globalChartData, globalChartOptions)
    slides.push(globalSlide)
  }

  const allocations = configuration.observation.allocationsCharts.allocations

  if (allocations && allocations.length) {
    let allocationsSlide = pptx.addNewSlide(MasterSlide.Chart)

    allocationsSlide.addText(title, {
      placeholder: Placeholder.Title
    })

    const allocationsChartData = createAllocationsChartData(
      allocations,
      localAuthority.observations,
      years
    )

    const allocationsChartOptions: ChartOptions = {
      ...getChartBaseOptions(placeholderBodyOptions),
      showTitle: false,
	  chartColors: getChartColors(),
    }

    allocationsSlide.addChart(
      pptx.charts.BAR,
      allocationsChartData,
      allocationsChartOptions
    )

    slides.push(allocationsSlide)

    if (configuration.observation.allocationsCharts.onePerAllocation) {
      allocations.forEach((allocation, allocationIndex) => {
        const perAllocationSlide = pptx.addNewSlide(MasterSlide.Chart)

        perAllocationSlide.addText(title, {
          placeholder: Placeholder.Title
        })

        const label = AllocationDomain.allocationLabels[allocation]
        const perAllocationChartTitle = `Par dotation : ${label}`
        const perAllocationChartData: [ChartBarDataset] = [
          {
            labels: allocationsChartData[allocationIndex].labels.slice(),
            name: allocationsChartData[allocationIndex].name,
            values: allocationsChartData[allocationIndex].values.slice(),
          },
        ]
        const perAllocationChartOptions: ChartOptions = {
          ...getChartBaseOptions(placeholderBodyOptions),
          showLegend: false,
          title: perAllocationChartTitle,
          chartColors: [baseColor.hex().substr(1)],
          showValue:true,
          dataLabelFontBold:true,
        }

        perAllocationSlide.addChart(
          pptx.charts.BAR,
          perAllocationChartData,
          perAllocationChartOptions
        )

        slides.push(perAllocationSlide)
      })
    }

    const inhabitantsSlide = pptx.addNewSlide(MasterSlide.Chart)

    inhabitantsSlide.addText(title, {
      placeholder: Placeholder.Title
    })

    const inhabitantsChartData = createInhabitantsChartData(
      allocationsChartData,
      localAuthority.demographies
    )

    const inhabitantsChartOptions: ChartMultiTypeOptions = {
      ...getChartBaseOptions(placeholderBodyOptions, null),
      title: "Par habitant",
      catAxes: [{}, { catAxisHidden: true }],
      valAxes: [
        { valAxisLabelFormatCode: Constants.STRING_FORMAT_CURRENCY },
        {
          valAxisLabelFormatCode: "#,##0",
          valAxisLineShow: true,
          valGridLine: "none"
        }
      ],
      chartColors: getChartColors(),
    } as ChartMultiTypeOptions

    const populationData = createDemographyData(
      localAuthority.demographies,
      years
    )

    const populationChartOptions: ChartMultiTypeItemOptions = {
      chartColors: ["666666"],
      chartColorsOpacity: 50,
      lineSize: 1,
      secondaryCatAxis: true,
      secondaryValAxis: true
    }

    const inhabitantsCharts: ChartMultiTypeItem[] = [
      { data: inhabitantsChartData, type: pptx.charts.BAR },
      {
        data: populationData,
        options: populationChartOptions,
        type: pptx.charts.LINE
      }
    ]

    inhabitantsSlide.addChart(inhabitantsCharts, inhabitantsChartOptions)

    slides.push(inhabitantsSlide)

    if (
      configuration.observation.allocationsCharts.onePerAllocationPerInhabitant
    ) {
      allocations.forEach((allocation, allocationIndex) => {
        const perInhabitantSlide = pptx.addNewSlide(MasterSlide.Chart)

        perInhabitantSlide.addText(title, {
          placeholder: Placeholder.Title
        })

        const label = AllocationDomain.allocationLabels[allocation]
        const perInhabitantChartTitle = `Par dotation et par habitant : ${label}`
        const perInhabitantChartData: [ChartBarDataset] = [
          {
            labels: inhabitantsChartData[allocationIndex].labels.slice(),
            name: inhabitantsChartData[allocationIndex].name,
            values: inhabitantsChartData[allocationIndex].values.slice(),
          }
        ]
        const perInhabitantChartOptions: ChartOptions = {
          ...getChartBaseOptions(placeholderBodyOptions),
          showLegend: false,
          title: perInhabitantChartTitle,
          chartColors: [baseColor.hex().substr(1)],
          showValue:true,
          dataLabelFontBold:true,
        }

        perInhabitantSlide.addChart(
          pptx.charts.BAR,
          perInhabitantChartData,
          perInhabitantChartOptions
        )

        slides.push(perInhabitantSlide)
      })
    }
  }

  return { slides, title }
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/observation-generator/allocation-generator/index.ts